import axios, { AxiosProgressEvent } from "axios";

import {
  useDelete,
  useFetch,
  usePost,
  useUpdate,
} from "../utilities/react-query";
import { request } from "./index";

const toursURL = "/web-twinprocms/360-tours";

/* =========================================================================
 * TypeScript Interfaces for 360 Video Scenes
 * ========================================================================= */

export interface PresignVideoScenePayload {
  filename: string;
  contentType?: string;
  content_type?: string;
  size?: number;
}

export interface PresignedPostData {
  key: string;
  url: string;
  fields: Record<string, string>;
  maxSizeBytes: number;
}

export interface UploadVideoToS3Progress {
  loaded: number;
  total?: number;
  percentage: number;
}

export interface UploadVideoToS3Options {
  url: string;
  fields: Record<string, string>;
  file: File | Blob;
  signal?: AbortSignal;
  onProgress?: (progress: UploadVideoToS3Progress) => void;
}

export interface RegisterVideoScenePayload {
  key: string;
  poster: File | Blob;
  name?: string;
}

export interface VideoSceneRegistrationData {
  id: number;
  title: string;
  path: string;
  yaw: number;
  pitch: number;
  fov: number;
  mediaType: "video";
  video: string;
}

export interface UploadAndRegisterVideoSceneOptions {
  tourId: number;
  videoFile: File;
  posterBlob: Blob;
  sceneName?: string;
  signal?: AbortSignal;
  onProgress?: (progress: UploadVideoToS3Progress) => void;
}

/* =========================================================================
 * 360 Video Scene Direct API Functions
 * ========================================================================= */

/**
 * Request an S3 presigned POST policy from the backend for direct upload.
 * Enforces the strict 300,000,000-byte limit on the server.
 */
export const presignVideoScene = async (
  tourId: number,
  payload: PresignVideoScenePayload,
  signal?: AbortSignal
): Promise<PresignedPostData> => {
  const contentType =
    payload.content_type || payload.contentType || "video/mp4";
  const response = await request.post<{ data: PresignedPostData }>(
    `${toursURL}/${tourId}/scenes/video/presign`,
    {
      filename: payload.filename,
      content_type: contentType,
      contentType: contentType,
      size: payload.size,
    },
    { signal }
  );
  return response.data?.data;
};

/**
 * Upload a video file directly to Amazon S3 using a presigned POST policy.
 *
 * CRITICAL REQUIREMENTS:
 * 1. Uses raw `axios` without CMS application interceptors to avoid sending
 *    unauthorized Authorization / Bearer headers that S3 presigned POST policies reject.
 * 2. Every field from the policy `fields` object is appended to FormData first.
 * 3. The `file` field is appended LAST per S3 presigned POST specification.
 * 4. Supports `signal` (AbortSignal) for cancellation and `onUploadProgress`.
 */
export const uploadVideoToS3 = async ({
  url,
  fields,
  file,
  signal,
  onProgress,
}: UploadVideoToS3Options): Promise<void> => {
  const formData = new FormData();

  // S3 Presigned POST requires all policy fields (key, policy, signature, etc.)
  // to be appended before the file field.
  if (fields && typeof fields === "object") {
    for (const [k, v] of Object.entries(fields)) {
      formData.append(k, v);
    }
  }

  // The file MUST be the final field appended to FormData.
  formData.append("file", file);

  try {
    await axios.post(url, formData, {
      signal,
      headers: {
        // Leave Content-Type unset so the browser generates the multipart boundary
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (onProgress) {
          const loaded = progressEvent.loaded;
          const total =
            progressEvent.total ?? (file instanceof Blob ? file.size : undefined);
          const percentage =
            total && total > 0 ? Math.min(100, Math.round((loaded * 100) / total)) : 0;
          onProgress({ loaded, total, percentage });
        }
      },
    });
  } catch (error: any) {
    if (axios.isCancel(error) || error?.name === "CanceledError" || signal?.aborted) {
      const abortErr = new Error("Video upload cancelled.");
      abortErr.name = "AbortError";
      throw abortErr;
    }

    // S3 XML error parsing if available
    const status = error?.response?.status;
    const responseData = error?.response?.data;
    if (status) {
      let s3Detail = "";
      if (typeof responseData === "string") {
        const match = responseData.match(/<Message>(.*?)<\/Message>/i);
        if (match?.[1]) {
          s3Detail = `: ${match[1]}`;
        }
      }
      throw new Error(`S3 direct upload failed with status ${status}${s3Detail}`);
    }
    throw error;
  }
};

/**
 * Register a newly uploaded 360 video scene with the CMS backend.
 * Submits the verified S3 video key, the extracted poster frame blob, and scene name.
 */
export const registerVideoScene = async (
  tourId: number,
  payload: RegisterVideoScenePayload,
  signal?: AbortSignal
): Promise<VideoSceneRegistrationData> => {
  const formData = new FormData();
  formData.append("key", payload.key);
  formData.append("poster", payload.poster, "poster.jpg");
  if (payload.name) {
    formData.append("name", payload.name);
  }

  const response = await request.post<{
    message: string;
    data: VideoSceneRegistrationData;
  }>(`${toursURL}/${tourId}/scenes/video`, formData, {
    signal,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data?.data;
};

/**
 * High-level workflow orchestrator:
 * 1. Obtains presigned POST policy from backend (`POST /scenes/video/presign`).
 * 2. Uploads the video file directly to S3 via raw axios (with progress & cancellation).
 * 3. Registers the scene with the backend (`POST /scenes/video`) with poster frame.
 */
export const uploadAndRegisterVideoScene = async ({
  tourId,
  videoFile,
  posterBlob,
  sceneName,
  signal,
  onProgress,
}: UploadAndRegisterVideoSceneOptions): Promise<VideoSceneRegistrationData> => {
  if (signal?.aborted) {
    const abortErr = new Error("Video upload cancelled.");
    abortErr.name = "AbortError";
    throw abortErr;
  }

  // Step 1: Request presigned POST from backend
  const presigned = await presignVideoScene(
    tourId,
    {
      filename: videoFile.name,
      content_type: videoFile.type || "video/mp4",
      contentType: videoFile.type || "video/mp4",
      size: videoFile.size,
    },
    signal
  );

  if (!presigned?.url || !presigned?.key || !presigned?.fields) {
    throw new Error("Invalid presigned upload response received from server.");
  }

  // Step 2: Upload directly to S3 via presigned POST policy
  await uploadVideoToS3({
    url: presigned.url,
    fields: presigned.fields,
    file: videoFile,
    signal,
    onProgress,
  });

  // Step 3: Register newly uploaded video scene with extracted poster frame
  const registeredScene = await registerVideoScene(
    tourId,
    {
      key: presigned.key,
      poster: posterBlob,
      name: sceneName || videoFile.name.replace(/\.[^/.]+$/, ""),
    },
    signal
  );

  return registeredScene;
};

/* =========================================================================
 * React Query Hooks (Existing & Video Scene Extensions)
 * ========================================================================= */

export const useGetToursList = ({ title = "", limit, offset }: any) => {
  const response: any = useFetch<any[]>(
    `${toursURL}?limit=${limit}&offset=${offset}&title=${encodeURIComponent(title)}`
  );
  return {
    ...response,
    data: response?.data ?? {},
  };
};

export const useGetTourDetail = (id: number | null) => {
  const response: any = useFetch<any>(
    id ? `${toursURL}/${id}` : null,
    undefined,
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
  return {
    ...response,
    data: response?.data?.data,
  };
};

export const useCreateTour = () => usePost<any, any>(`${toursURL}/create`);

export const useUploadTourScene = (id: number | null) =>
  useUpdate<any, any>(`${toursURL}/${id}/scenes`, undefined, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

export const usePresignVideoSceneUpload = (tourId: number | null) =>
  usePost<any, any>(tourId ? `${toursURL}/${tourId}/scenes/video/presign` : "");

export const useRegisterVideoScene = (tourId: number | null) =>
  usePost<any, any>(tourId ? `${toursURL}/${tourId}/scenes/video` : "", undefined, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

export const useSaveTour = (id: number | null) =>
  useUpdate<any, any>(`${toursURL}/${id}/update`);

export const useDeleteTour = () => useDelete<any>(toursURL);

export const useGetToursDropdown = () => {
  const response: any = useFetch<any[]>(`${toursURL}?dropdown`);
  return {
    ...response,
    data: response?.data?.data ?? [],
  };
};
